import { Box, Button, Stack, Typography } from '@mui/material'
import type { SuggestedOrderModel } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'

interface Props {
  model: SuggestedOrderModel
  /**
   * The same switch `CatalogCardModel.showPrice` carries. `toSuggestedOrderModel`
   * already blanks the amounts when prices are hidden; this is what keeps the
   * empty strings and the `·` separator out of the layout.
   */
  showPrices: boolean
  /** The design's "Según tu pedido del 6 ago". */
  subtitle: string
  onAddAll: () => void
  disabled?: boolean
}

/**
 * The pre-computed basket (reference.html:767–783).
 *
 * It renders a model and does no arithmetic: the lines, the amounts and the
 * total come from `toSuggestedOrderModel`. Which skus are in the basket and in
 * what quantity is the page's decision, because it is the host's — nothing in
 * the domain proposes an order.
 */
export const SuggestedOrder = ({
  model,
  showPrices,
  subtitle,
  onAddAll,
  disabled = false,
}: Props) => (
  <Box
    component="section"
    aria-label="Sugerido para ti"
    sx={{
      bgcolor: YALO_SEMANTIC.bgSurface,
      border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
      borderRadius: `${YALO_RADIUS.lg}px`,
      p: 1.75,
      boxShadow: YALO_SHADOW.xs,
    }}
  >
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Sugerido para ti
        </Typography>
        <Typography sx={{ fontSize: 11.5 }} color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>

      {model.lines.map((line) => (
        <Stack key={line.sku} direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 34,
              height: 34,
              flex: 'none',
              borderRadius: `${YALO_RADIUS.sm}px`,
              border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
              bgcolor: YALO_SEMANTIC.bgSurface,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={line.imageUrl}
              alt=""
              sx={{ width: '82%', height: '82%', objectFit: 'contain' }}
            />
          </Box>
          <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, lineHeight: '16px' }}>
            {line.title}
          </Typography>
          <Typography sx={{ fontSize: 12.5, flex: 'none' }} color="text.secondary">
            ×{line.quantity}
          </Typography>
          {showPrices && (
            <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 'none' }}>
              {line.amountText}
            </Typography>
          )}
        </Stack>
      ))}

      <Button
        fullWidth
        onClick={onAddAll}
        disabled={disabled}
        sx={{
          height: 42,
          borderRadius: `${YALO_RADIUS.pill}px`,
          bgcolor: 'primary.light',
          color: 'primary.dark',
          fontWeight: 600,
          fontSize: 13.5,
          '&:hover': { bgcolor: 'primary.light', filter: 'brightness(0.97)' },
        }}
      >
        {showPrices ? `Agregar todo · ${model.totalText}` : 'Agregar todo'}
      </Button>
    </Stack>
  </Box>
)
